import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import i18next from 'i18next'

import { error, success } from 'src/components/system_wide/notification'

import axios from 'src/lib/axios'

const { t } = i18next

const initialState = {
  total: 0,
  metadata: [],
  errors: {},
  show_form: false,
  current_metadata: {
    id: null,
    name: '',
    filename: '',
    description: '',
    mime_type: '',
    ext: '',
    exif: {},
    created_by: null,
    created_by_name: '',
    appointment: null,
  },
  pending: false,
}

export const getMetadata = createAsyncThunk(
  'metadata/list',
  async ({ offset, limit, sortData, filter, filtered_brands, filtered_users }, thunkApi) => {
    try {
      let { total: currentTotal } = thunkApi.getState().metadata
      if (offset > currentTotal) return

      let { sort_column, sort_direction } = sortData
      let response = await axios({
        url: `/api/appointments/metadata`,
        params: {
          offset,
          limit,
          sort_column,
          sort_direction,
          filter,
          filtered_brands: filtered_brands.join(','),
          filtered_users: filtered_users.join(','),
        },
      })
      let { total, metadata } = response.data
      return { total, metadata, reset: offset === 0 }
    } catch (err) {
      error(t('store.metadata.get_metadata_error', 'Impossibile recuperare i metadati'))
      return thunkApi.rejectWithValue()
    }
  }
)

export const deleteMetadata = createAsyncThunk('metadata/delete', async ({ id }, thunkApi) => {
  try {
    await axios({ url: `/api/appointments/metadata/${id}`, method: 'delete' })
    success(t('store.metadata.delete_metadata_success', 'Metadato eliminato con successo'))
    thunkApi.dispatch(resetCurrentMetadata())
  } catch (err) {
    error(t('store.metadata.delete_metadata_error', 'Impossibile eliminare il metadato'))
    return thunkApi.rejectWithValue()
  }
})

export const metadataSlice = createSlice({
  name: 'metadata',
  initialState,

  reducers: {
    setCurrentMetadata: (state, action) => {
      let { id, name, filename, description, mime_type, ext, exif, created_by, created_by_name, appointment } =
        action.payload
      let {
        id: id_appointment,
        type,
        code,
        created_by: appointment_created_by,
        dealer_brand_id,
        date,
        note,
        photos,
        dealer_brand,
        contract,
      } = appointment
      let { dealer, brand } = dealer_brand

      state.current_metadata.id = id
      state.current_metadata.name = name
      state.current_metadata.filename = filename
      state.current_metadata.description = description
      state.current_metadata.mime_type = mime_type
      state.current_metadata.ext = ext
      state.current_metadata.exif = exif
      state.current_metadata.created_by = created_by
      state.current_metadata.created_by_name = created_by_name
      state.current_metadata.appointment = {
        id: id_appointment,
        dealer_brand_id,
        code,
        type,
        created_by: appointment_created_by.id,
        created_by_name: appointment_created_by.first_name + ' ' + appointment_created_by.last_name,
        dealer_id: dealer.id,
        piva: dealer.piva,
        business_name: dealer.business_name,
        date,
        note,
        photos,
        contract,
        brand_id: brand.id,
        brand_name: brand.name,
        place_id: dealer.place_id,
        address: dealer.address,
        merchandise_category_id: dealer.merchandise_category_id,
        contacts: dealer.contacts,
      }
    },

    toggleShowForm: state => {
      state.show_form = !state.show_form
    },
    resetCurrentMetadata: state => {
      state.current_metadata = initialState.current_metadata
    },
    resetMetadata: () => initialState,
  },

  extraReducers: {
    [getMetadata.fulfilled]: (state, action) => {
      if (!action?.payload) return
      let { total, metadata } = action.payload
      state.total = total
      metadata = metadata.map(m => {
        m.exif = JSON.parse(`${m.exif}`)
        if (m?.exif?.DateTime) {
          m.exif.DateTime = m.exif.DateTime.replace(/^(\d{4}):(\d{2}):(\d{2})/, '$1-$2-$3')
        }
        return m
      })
      state.metadata = action.payload.reset ? [...metadata] : [...state.metadata, ...metadata]

      state.pending = false
    },
    [getMetadata.rejected]: state => {
      state.total = initialState.total
      state.metadata = initialState.metadata
      state.pending = false
    },
    [getMetadata.pending]: state => {
      state.pending = true
    },
    [deleteMetadata.fulfilled]: state => {
      state.pending = false
    },
    [deleteMetadata.rejected]: state => {
      state.pending = false
    },
    [deleteMetadata.pending]: state => {
      state.pending = true
    },
  },
})

export const { setCurrentMetadata, resetCurrentMetadata, toggleShowForm, resetMetadata } = metadataSlice.actions
export default metadataSlice.reducer
